import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import CardTravelOutlined from "@mui/icons-material/CardTravelOutlined";
import SwapCallsOutlined from "@mui/icons-material/SwapCallsOutlined";
import FactCheckOutlined from "@mui/icons-material/FactCheckOutlined";
import PageviewOutlined from "@mui/icons-material/PageviewOutlined";
import FilterTiltShiftOutlined from "@mui/icons-material/FilterTiltShiftOutlined";
import TrendingUpOutlined from "@mui/icons-material/TrendingUpOutlined";
import ElectricBoltOutlined from "@mui/icons-material/ElectricBoltOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";
import HubOutlined from "@mui/icons-material/HubOutlined";
import GroupOutlined from "@mui/icons-material/GroupOutlined";
import GppGoodOutlined from "@mui/icons-material/GppGoodOutlined";
import GradientIcon from "../components/GradientIcon";
import CheckIcon from "@mui/icons-material/Check";
import HaveQuestions from "../components/HaveQuestions";
import diffBg from "../assets/Differentiation-bg.png";
import halfCircles from "../assets/half-circles.png";

const gradientHoverSx = {
  position: "relative",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
  transition: "all 0.3s ease",
  "&:hover::before": {
    opacity: 1,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "24px",
    padding: "2px",
    background: "linear-gradient(90deg, #FF7FAC, #5834BA)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
};

const Differentiation = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box /*sx={{ backgroundColor: "#F9F9F9" }}*/>
      <Helmet>
        <title>DAM Journey - Differentiation</title>
        <meta
          name="description"
          content="What Makes Us Different in DAM, We don’t just implement DAM—we transform how your organization works with digital assets through strategy, execution, and continuous optimization"
        />
        <meta
          property="keywords"
          content="DAM, DAM Journey, DAM Practice, DAM Consultant, DAM Consulting, DAM Practitioner, DAM Service, DAM Product, DAM Discovery, DAM Implementation, DAM Deployment, What Makes Us Different in DAM"
        />
      </Helmet>

      <Box /*sx={{ maxWidth: "1440px" }}*/>
        {/* Hero Banner Section */}
        <Box
          sx={{
            width: "100%",
            height: "350px",
            background: "linear-gradient(0deg, #2A2177 0%, #0624BE 100%)",
            display: "flex",
            alignItems: "center",
            px: "84px",
            py: "60px",
            overflow: "hidden",
            position: "relative",
            "@media (max-width: 1200px)": {
              px: "24px",
            },
            "@media (max-width: 780px)": {
              flexDirection: "column",
              textAlign: "center",
              px: "16px",
              pt: "32px",
              pb: 2,
              height: "420px",
            },
          }}
        >
          {/* Left Text Content */}
          <Box
            sx={{
              flex: 2,
              zIndex: 1,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: "2.5rem",
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 1.3,
                mb: 2,
                "@media (max-width: 1200px)": {
                  fontSize: "1.75rem",
                  fontWeight: "semiBold",
                },
                "@media (max-width: 780px)": {
                  fontSize: "1.5rem",
                  mb: 1,
                },
              }}
            >
              What Makes Us Different in DAM
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "regular",
                color: "#ffffff",
                lineHeight: 1.7,
                maxWidth: "624px",
                "@media (max-width: 1200px)": {
                  fontSize: "18px",
                },
                "@media (max-width: 780px)": {
                  fontSize: "16px",
                  mb: 2,
                },
              }}
            >
              We don’t just implement DAM—we transform how your
              organization works with digital assets through
              strategy, execution, and continuous optimization.
            </Typography>
          </Box>

          {/* Right Diff-bg Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              position: "relative",
            }}
          >
            {/* Background Half Circles */}
            <Box
              component="img"
              src={halfCircles}
              alt=""
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: "599px",
                height: "350px",
                objectFit: "contain",
                left: -20,
                "@media (max-width: 1200px)": {
                  left: 0,
                },
                "@media (max-width: 780px)": {
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "370px",
                  height: "auto",
                },
                zIndex: 0,
              }}
            />
            <Box
              component="img"
              src={diffBg}
              // alt="Differentiation Infographic"
              sx={{
                position: "relative",
                zIndex: 1,
                width: "510px",
                height: "300px",
                "@media (max-width: 1200px)": {
                  width: "388px",
                  height: "228px",
                },
                "@media (max-width: 780px)": {
                  fontSize: "18px",
                  width: "317px",
                  height: "187px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Our Core Differentiators Section */}
        <Box
          sx={{
            py: "48px",
            px: "84px",
            "@media (max-width: 1200px)": {
              px: "24px",
            },
            "@media (max-width: 780px)": {
              px: "16px",
            },
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              mb: 2,
              "@media (max-width: 1200px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 780px)": {
                fontSize: "2rem",
                // width: "328px",
              },
            }}
          >
            Our Core Differentiators
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
              "@media (max-width: 1200px)": {
                fontSize: "16px",
                // width: "720px",
              },
              "@media (max-width: 780px)": {
                fontSize: "16px",
                // width: "328px",
              },
            }}
          >
            We stand apart through a combination of strategic
            thinking, technical expertise, and user-centric
            design. Our approach ensures scalable, efficient,
            and future-ready Digital Asset Management solutions
            tailored to your business needs.
          </Typography>

          {/* Business-First & End-to-End & Real-World Cards */}
          <Box
            sx={{
              "@media (max-width: 1200px)": {
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "24px",
                // width: "720px",
              },
              "@media (max-width: 780px)": {
                gridTemplateColumns: "repeat(1, 1fr)",
                // width: "328px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 3,
                mb: 3,
                justifyContent: "center",
                "@media (max-width: 1200px)": {
                  display: "contents",
                  width: "100%",
                },
                "@media (max-width: 780px)": {
                  // display: "flex",
                  // flexDirection: "column",
                  // alignItems: "center",
                  // gap: 6,
                },
              }}
            >
              {/* Business-First Approach Card */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "#ffffff",
                  borderRadius: "24px",
                  pb: "32px",
                  pt: "48px",
                  px: "48px",
                  boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                  ...gradientHoverSx,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    mb: 2,
                  }}
                >
                  <GradientIcon
                    icon={CardTravelOutlined}
                    size={40}
                    bordered
                    boxSize={80}
                    borderRadius={24}
                    borderWidth={2}
                    angle="90"
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center"
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        color: "#000000",
                        lineHeight: 2,
                        textAlign: "center",
                        "@media (max-width: 1200px)": {
                          fontSize: "1.2rem",
                        },
                      }}
                    >
                      Business-First Approach
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 400,
                        color: "#666666",
                        lineHeight: 1.6,
                        textAlign: "center",
                      }}
                    >
                      We align DAM strategy with your business
                      goals-not just technology.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* End-to-End Ownership Card */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "#ffffff",
                  borderRadius: "24px",
                  pb: "32px",
                  pt: "48px",
                  px: "48px",
                  boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                  ...gradientHoverSx,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    mb: 2,
                  }}
                >
                  <GradientIcon
                    icon={SwapCallsOutlined}
                    size={40}
                    bordered
                    boxSize={80}
                    borderRadius={24}
                    borderWidth={2}
                    angle="90"
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center"
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        color: "#000000",
                        lineHeight: 2,
                        textAlign: "center",
                        "@media (max-width: 1200px)": {
                          fontSize: "1.2rem",
                        },
                      }}
                    >
                      End-to-End Ownership
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 400,
                        color: "#666666",
                        lineHeight: 1.6,
                        textAlign: "center",
                      }}
                    >
                      From consulting to Implementation and support-
                      we handle everything.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* Real-World Execution Card */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "#ffffff",
                  borderRadius: "24px",
                  pb: "32px",
                  pt: "48px",
                  px: "48px",
                  boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                  ...gradientHoverSx,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    mb: 2,
                  }}
                >
                  <GradientIcon
                    icon={FactCheckOutlined}
                    size={40}
                    bordered
                    boxSize={80}
                    borderRadius={24}
                    borderWidth={2}
                    angle="90"
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center"
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        color: "#000000",
                        lineHeight: 2,
                        textAlign: "center",
                        "@media (max-width: 1200px)": {
                          fontSize: "1.2rem",
                        },
                      }}
                    >
                      Real-World Execution
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 400,
                        color: "#666666",
                        lineHeight: 1.6,
                        textAlign: "center",
                      }}
                    >
                      Practical solutions tested in real
                      business environments.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Vendor Neutral (Product Agnostic) & User-Centric Design & Scalable Solutions Cards */}
            <Box
              sx={{
                display: "flex",
                gap: "24px",
                justifyContent: "center",
                "@media (max-width: 1200px)": {
                  display: "contents",
                  width: "100%",
                },
                "@media (max-width: 780px)": {
                  // display: "flex",
                  // flexDirection: "column",
                  // alignItems: "center",
                },
              }}
            >
              {/* Vendor Neutral (Product Agnostic) Card */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "#ffffff",
                  borderRadius: "24px",
                  pb: "32px",
                  pt: "48px",
                  px: "48px",
                  boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                  ...gradientHoverSx,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    mb: 2,
                  }}
                >
                  <GradientIcon
                    icon={PageviewOutlined}
                    size={40}
                    bordered
                    boxSize={80}
                    borderRadius={24}
                    borderWidth={2}
                    angle="90"
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center"
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        color: "#000000",
                        lineHeight: 2,
                        textAlign: "center",
                        "@media (max-width: 1200px)": {
                          fontSize: "1.2rem",
                        },
                      }}
                    >
                      Vendor Neutral (Product Agnostic)
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 400,
                        color: "#666666",
                        lineHeight: 1.6,
                        textAlign: "center",
                      }}
                    >
                      We recommend what's best for you-not tied
                      to any tool.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* User-Centric Design Card */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "#ffffff",
                  borderRadius: "24px",
                  pb: "32px",
                  pt: "48px",
                  px: "48px",
                  boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                  ...gradientHoverSx,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    mb: 2,
                  }}
                >
                  <GradientIcon
                    icon={FilterTiltShiftOutlined}
                    size={40}
                    bordered
                    boxSize={80}
                    borderRadius={24}
                    borderWidth={2}
                    angle="90"
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center"
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        color: "#000000",
                        lineHeight: 2,
                        textAlign: "center",
                        "@media (max-width: 1200px)": {
                          fontSize: "1.2rem",
                        },
                      }}
                    >
                      User-Centric Design
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 400,
                        color: "#666666",
                        lineHeight: 1.6,
                        textAlign: "center",
                      }}
                    >
                      Focus on usability, adoption, and real user
                      workflows.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {/* Scalable Solutions Card */}
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: "#ffffff",
                  borderRadius: "24px",
                  pb: "32px",
                  pt: "48px",
                  px: "48px",
                  boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                  ...gradientHoverSx,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 3,
                    mb: 2,
                  }}
                >
                  <GradientIcon
                    icon={TrendingUpOutlined}
                    size={40}
                    bordered
                    boxSize={80}
                    borderRadius={24}
                    borderWidth={2}
                    angle="90"
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center"
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1.5rem",
                        fontWeight: 700,
                        color: "#000000",
                        lineHeight: 2,
                        textAlign: "center",
                        "@media (max-width: 1200px)": {
                          fontSize: "1.2rem",
                        },
                      }}
                    >
                      Scalable Solutions
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 400,
                        color: "#666666",
                        lineHeight: 1.6,
                        textAlign: "center",
                      }}
                    >
                      Built for today and future growth.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* The Outcome Section */}
        <Box
          sx={{
            pb: "48px",
            pt: "24px",
            px: "84px",
            "@media (max-width: 1200px)": {
              px: "24px",
            },
            "@media (max-width: 780px)": {
              px: "16px",
            },
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "bold",
              textAlign: "left",
              color: "#2A2177",
              mb: 3,
              "@media (max-width: 1200px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 780px)": {
                fontSize: "30px",
              },
            }}
          >
            The Outcome
          </Typography>

          {/* Cards Container */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
              gap: 3,
              "@media (max-width: 1200px)": {
                width: "720px",
                display: "contents",
                flexWrap: "wrap",
              },
              "@media (max-width: 780px)": {
                width: "328px",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                gap: 3,
                flexWrap: "wrap",
                // margin: "0 auto",
                //  maxWidth: "1270px",
              }}
            >
              {/* Faster Time to Value */}
              <Box
                sx={{
                  width: "612px",
                  height: "84px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  py: "16px",
                  gap: "16px",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    background: "linear-gradient(135deg, #724FD300 0%, #FC86AF1A 100%)",
                    boxShadow: "0px 4px 16px rgba(0,0,0,0.03)",
                  },
                  "@media (max-width: 1200px)": {
                    width: "100%",
                    height: "80px",
                  },
                  "@media (max-width: 780px)": {
                    width: "100%",
                    height: "120px",
                  },
                }}
              >
                <GradientIcon
                  icon={ElectricBoltOutlined}
                  size={24}
                  bordered
                  boxSize={48}
                  borderRadius={8}
                  borderWidth={2}
                  angle="90"
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      mb: 0.5,
                      color: "#000000",
                      "@media (max-width: 1200px)": {
                        fontSize: "1rem",
                      },
                    }}
                  >
                    Faster Time to Value
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.4,
                      "@media (max-width: 1200px)": {
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    Accelerated implementation and adoption mean
                    you start realizing ROI from your DAM
                    investment much sooner.
                  </Typography>
                </Box>
              </Box>

              {/* Seamless Cross-Team Collaboration */}
              <Box
                sx={{
                  width: "612px",
                  height: "84px",
                  borderRadius: "24px",
                  display: "flex",
                  alignItems: "center",
                  py: "16px",
                  gap: "16px",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    background: "linear-gradient(135deg, #724FD300 0%, #FC86AF1A 100%)",
                    boxShadow: "0px 4px 16px rgba(0,0,0,0.03)",
                  },
                  "@media (max-width: 1200px)": {
                    width: "100%",
                    height: "80px",
                  },
                  "@media (max-width: 780px)": {
                    width: "100%",
                    height: "120px",
                  },
                }}
              >
                <GradientIcon
                  icon={Groups2OutlinedIcon}
                  size={24}
                  bordered
                  boxSize={48}
                  borderRadius={8}
                  borderWidth={2}
                  angle="90"
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      mb: 0.5,
                      color: "#000000",
                      "@media (max-width: 1200px)": {
                        fontSize: "1rem",
                      },
                    }}
                  >
                    Seamless Cross-Team Collaboration
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.4,
                      "@media (max-width: 1200px)": {
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    Aligned workflows and integrated systems
                    enable teams to find, use, and share
                    assets effortlessly.
                  </Typography>
                </Box>
              </Box>

              {/* Improved Asset Governance & Control */}
              <Box
                sx={{
                  width: "612px",
                  height: "84px",
                  borderRadius: "24px",
                  display: "flex",
                  alignItems: "center",
                  py: "16px",
                  gap: "16px",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    background: "linear-gradient(135deg, #724FD300 0%, #FC86AF1A 100%)",
                    boxShadow: "0px 4px 16px rgba(0,0,0,0.03)",
                  },
                  "@media (max-width: 1200px)": {
                    width: "100%",
                    height: "80px",
                  },
                  "@media (max-width: 780px)": {
                    width: "100%",
                    height: "120px",
                  },
                }}
              >
                <GradientIcon
                  icon={VerifiedUserOutlined}
                  size={24}
                  bordered
                  boxSize={48}
                  borderRadius={8}
                  borderWidth={2}
                  angle="90"
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      mb: 0.5,
                      color: "#000000",
                      "@media (max-width: 1200px)": {
                        fontSize: "1rem",
                      },
                    }}
                  >
                    Improved Asset Governance & Control
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.4,
                      "@media (max-width: 1200px)": {
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    Clear structures, metadata strategies,
                    and processes ensure your digital
                    assets are organized, secure, and always
                    accessible.
                  </Typography>
                </Box>
              </Box>

              {/* Scalable & Future-Ready DAM Ecosystem */}
              <Box
                sx={{
                  width: "612px",
                  height: "84px",
                  borderRadius: "24px",
                  display: "flex",
                  alignItems: "center",
                  py: "16px",
                  gap: "16px",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  position: "relative",
                  overflow: "hidden",
                  "&:hover": {
                    background: "linear-gradient(135deg, #724FD300 0%, #FC86AF1A 100%)",
                    boxShadow: "0px 4px 16px rgba(0,0,0,0.03)",
                  },
                  "@media (max-width: 1200px)": {
                    width: "100%",
                    height: "80px",
                  },
                  "@media (max-width: 780px)": {
                    width: "100%",
                    height: "120px",
                  },
                }}
              >
                <GradientIcon
                  icon={HubOutlined}
                  size={24}
                  bordered
                  boxSize={48}
                  borderRadius={8}
                  borderWidth={2}
                  angle="90"
                />
                <Box>
                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 700,
                      mb: 0.5,
                      color: "#000000",
                      "@media (max-width: 1200px)": {
                        fontSize: "1rem",
                      },
                    }}
                  >
                    Scalable & Future-Ready DAM Ecosystem
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.4,
                      "@media (max-width: 1200px)": {
                        fontSize: "0.875rem",
                      },
                    }}
                  >
                    A solution designed to evolve with your
                    business, supporting growth, new use cases,
                    and changing technology needs.
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* DAM Journey & Others Box */}
            <Box
              sx={{
                display: "flex",
                textAlign: "center",
                width: "612px",
                height: "288px",
                background: "#D8D3FF",
                borderRadius: "16px",
                overflow: "hidden",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                "@media (max-width: 1200px)": {
                  width: "720px",
                  mt: "30px",
                  mx: "auto",
                },
                "@media (max-width: 780px)": {
                  width: "100%",//"328px",
                  flexDirection: "column",
                  height: "576px",
                  background: "#D8D3FF",
                },
              }}
            >
              {/* LEFT COLUMN */}
              <Box
                sx={{
                  width: "312px",
                  height: "288px",
                  borderRadius: "16px",
                  overflow: "hidden",
                  "@media (max-width: 1200px)": {
                    width: "360px",
                  },
                  "@media (max-width: 780px)": {
                    width: "100%",//"328px",
                    height: "288px",
                  },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    background: "#384DB1",
                    color: "#fff",
                    height: "64px",
                    px: "24px",
                    py: "16px",
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    sx={{
                      fontSize: "1.5rem",
                    }}
                  >
                    DAM Journey
                  </Typography>
                </Box>

                {/* Rows */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "32px",
                    py: "16px",
                    color: "#fff",
                    height: "56px",
                    backgroundColor:  "#3648A7",
                    fontSize: "1rem",
                  }}
                >
                  <CheckIcon sx={{ color: "#FFD54F", mr: 1 }} />
                  Hands-on execution
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "32px",
                    py: "16px",
                    color: "#fff",
                    height: "56px",
                    backgroundColor:  "#3945A4",
                    fontSize: "1rem",
                  }}
                >
                  <CheckIcon sx={{ color: "#FFD54F", mr: 1 }} />
                  Continuous partnership
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "32px",
                    py: "16px",
                    color: "#fff",
                    height: "56px",
                    backgroundColor:  "#373C95",
                    fontSize: "1rem",
                  }}
                >
                  <CheckIcon sx={{ color: "#FFD54F", mr: 1 }} />
                  Strategy + UX focused
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "32px",
                    py: "16px",
                    color: "#fff",
                    height: "56px",
                    backgroundColor:  "#39388F",
                    fontSize: "1rem",
                  }}
                >
                  <CheckIcon sx={{ color: "#FFD54F", mr: 1 }} />
                  Ongoing optimization
                </Box>
              </Box>

              {/* RIGHT COLUMN */}
              <Box
                sx={{
                  width: "300px",
                  height: "288px",
                  "@media (max-width: 1200px)": {
                    width: "360px",
                  },
                  "@media (max-width: 780px)": {
                    width: "100%",//"328px",
                    height: "288px",
                    borderRadius: "16px",
                    overflow: "hidden",
                  },
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    background: "#D8D3FF",
                    color: "#2A2177",
                    height: "64px",
                    px: 3,
                    py: 2,
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    sx={{
                      fontSize: "1.5rem",
                    }}
                  >
                    Others
                  </Typography>
                </Box>

                {/* Rows */}
                <Box
                  sx={{
                    px: "48px",
                    py: "16px",
                    height: "56px",
                    color: "#2A2177",
                    backgroundColor: "#E6E3FF",
                    textAlign: "left",
                    fontSize: "1rem",
                  }}
                >
                  Static consulting
                </Box>
                <Box
                  sx={{
                    px: "48px",
                    py: "16px",
                    color: "#2A2177",
                    backgroundColor: "#D8D3FF",
                    textAlign: "left",
                    fontSize: "1rem",
                  }}
                >
                  One-time delivery
                </Box>
                <Box
                  sx={{
                    px: "48px",
                    py: "16px",
                    height: "56px",
                    color: "#2A2177",
                    backgroundColor: "#E6E3FF",
                    textAlign: "left",
                    fontSize: "1rem",
                  }}
                >
                  Tool-focused
                </Box>
                <Box
                  sx={{
                    px: "48px",
                    py: "16px",
                    height: "56px",
                    color: "#2A2177",
                    backgroundColor: "#D8D3FF",
                    textAlign: "left",
                    fontSize: "1rem",
                  }}
                >
                  Limited support
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Real Impact Section */}
        <Box
          sx={{
            py: "48px",
            px: "84px",
            "@media (max-width: 1200px)": {
              px: "24px",
            },
            "@media (max-width: 780px)": {
              px: "16px",
            },
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              mb: 2,
              "@media (max-width: 1200px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 780px)": {
                fontSize: "2rem",
              },
            }}
          >
            Real Impact
          </Typography>
          <Typography
            sx={{
              width: "100%",
              fontSize: "18px",
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
              "@media (max-width: 1200px)": {
                fontSize: "16px",                
              },              
            }}
          >
            We deliver measurable results that drive efficiency,
            improve workflows, and maximize the value of your
            digital assets across the organization.
          </Typography>
          {/* Cards Container */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexDirection: "row",
              flexWrap: "wrap",
              pl: 14,
              borderRadius: "16px",
              boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
              backgroundColor: "#FFFFFF",
              // maxWidth: "1272px",
              width: "100%",
              // margin: "0 auto",
              height: "88px",
              //  maxWidth: "1270px",
              "@media (max-width: 1200px)": {
                pl: "48px",
              },
              "@media (max-width: 780px)": {
                height: "auto",
                flexDirection: "column",
                alignItems: "stretch",
                gap: "16px",
                padding: "24px",
              },
            }}
          >
            {/* Faster */}
            <Box
              sx={{
                width: "380.67px",
                height: "86px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flex: 1,

                "@media (max-width: 1200px)": {
                  width: "218.67px",
                  px: "1px",
                  gap: "8px",
                },
                "@media (max-width: 780px)": {
                  width: "328px",
                  gap: "16px",
                  py: "8px",
                },
              }}
            >
              <GradientIcon
                icon={ElectricBoltOutlined}
                size={24}
                bordered
                boxSize={40}
                borderRadius={8}
                borderWidth={2}
                angle="90"
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    mb: 0.5,
                    color: "#000000",
                    "@media (max-width: 1200px)": {
                      fontSize: "14px",
                    },
                  }}
                >
                  <b>Faster</b> Time-to-Value
                </Typography>
              </Box>
              <Divider
                orientation="vertical"
                sx={{
                  height: "56px",
                  px: "32px",
                  py: "8px",
                  "@media (max-width: 1200px)": {
                    display: "none",
                  },
                }}
              />
            </Box>
            {/* Improved */}
            <Box
              sx={{
                width: "380.67px",
                height: "86px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flex: 1,

                "@media (max-width: 1200px)": {
                  width: "218.67px",
                  px: "1px",
                  gap: "8px",
                },
                "@media (max-width: 780px)": {
                  width: "328px",
                  gap: "16px",
                  py: "8px",
                },
              }}
            >
              <GradientIcon
                icon={GroupOutlined}
                size={24}
                bordered
                boxSize={40}
                borderRadius={8}
                borderWidth={2}
                angle="90"
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    mb: 0.5,
                    color: "#000000",
                    "@media (max-width: 1200px)": {
                      fontSize: "14px",
                    },
                  }}
                >
                  <b>Improved</b> Collaboration
                </Typography>
              </Box>
              <Divider
                orientation="vertical"
                sx={{
                  height: "56px",
                  px: "32px",
                  py: "8px",
                  "@media (max-width: 1200px)": {
                    display: "none",
                  },
                }}
              />
            </Box>
            {/* Better */}
            <Box
              sx={{
                width: "380.67px",
                height: "86px",
                display: "flex",
                alignItems: "center",
                gap: "16px",
                flex: 1,

                "@media (max-width: 1200px)": {
                  width: "218.67px",
                  px: "1px",
                  gap: "8px",
                },
                "@media (max-width: 780px)": {
                  width: "328px",
                  gap: "16px",
                  py: "8px",
                },
              }}
            >
              <GradientIcon
                icon={GppGoodOutlined}
                size={24}
                bordered
                boxSize={40}
                borderRadius={8}
                borderWidth={2}
                angle="90"
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    mb: 0.5,
                    color: "#000000",
                    "@media (max-width: 1200px)": {
                      fontSize: "14px",
                    },
                  }}
                >
                  <b>Better</b> Asset Governance
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Have Questions Section */}
        <HaveQuestions />
      </Box>
    </Box>
  );
};

export default Differentiation;
